/**
 * ConcurrencyRenderer - Renderer for concurrency control settings
 *
 * Allows configuring the degree of parallelism for actions/triggers.
 * - Actions: 1-100 parallel runs
 * - Triggers: 1-50 parallel runs with optional waiting queue
 */

import { makeStyles, tokens, Text, Switch, Input, Field, SpinButton, type SpinButtonChangeEvent, type SpinButtonOnChangeData } from '@fluentui/react-components';
import type { ConcurrencySettings } from '@/types/nodeSettings';
import { DEFAULT_CONCURRENCY } from '@/types/nodeSettings';
import type { SettingRendererProps } from './registry';
import { registerSettingRenderer } from './registry';

import {
  settingContainerStyle,
  settingDescriptionStyle,
  settingHeaderStyle,
  settingLabelStyle,
} from './settingRendererStyles';

const useStyles = makeStyles({
  container: settingContainerStyle,
  header: settingHeaderStyle,
  labelContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flex: 1,
  },
  label: settingLabelStyle,
  description: settingDescriptionStyle,
  fields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalXS,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  hint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
    marginTop: tokens.spacingVerticalXS,
  },
});

export interface ConcurrencyRendererProps extends SettingRendererProps<ConcurrencySettings> {
  label?: string;
  description?: string;
  /** Whether this is for a trigger (affects max value and shows waiting runs) */
  isTrigger?: boolean;
}

/**
 * ConcurrencyRenderer component for concurrency control settings
 */
export const ConcurrencyRenderer = ({
  setting,
  onChange,
  readOnly = false,
  label = 'Concurrency Control',
  description = 'Limit the number of parallel executions',
  isTrigger = false,
}: ConcurrencyRendererProps) => {
  const styles = useStyles();

  // Don't render if setting is not supported
  if (!setting.isSupported) {
    return null;
  }

  const currentSettings = setting.value ?? DEFAULT_CONCURRENCY;
  const maxRuns = isTrigger ? 50 : 100;

  const handleEnabledChange = (_: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
    onChange({
      ...currentSettings,
      enabled: data.checked,
    });
  };

  const handleRunsChange = (_event: SpinButtonChangeEvent, data: SpinButtonOnChangeData) => {
    const value = data.value ?? (data.displayValue ? parseInt(data.displayValue, 10) : null);
    if (value !== null && !isNaN(value)) {
      onChange({
        ...currentSettings,
        runs: Math.min(Math.max(1, value), maxRuns),
      });
    }
  };

  const handleWaitingRunsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (isNaN(value) || event.target.value === '') {
      // Remove the property when empty
      const { maximumWaitingRuns: _removed, ...rest } = currentSettings;
      void _removed; // Explicitly mark as intentionally unused
      onChange(rest as ConcurrencySettings);
    } else {
      onChange({
        ...currentSettings,
        maximumWaitingRuns: Math.max(0, value),
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.labelContainer}>
          <Text className={styles.label}>{label}</Text>
          {description && <Text className={styles.description}>{description}</Text>}
        </div>
        <Switch
          checked={currentSettings.enabled}
          onChange={handleEnabledChange}
          disabled={readOnly}
          aria-label="Enable concurrency control"
        />
      </div>

      {currentSettings.enabled && (
        <div className={styles.fields}>
          <div className={styles.field}>
            <Field label="Degree of Parallelism" hint={`1-${maxRuns} parallel runs`}>
              <SpinButton
                value={currentSettings.runs}
                min={1}
                max={maxRuns}
                onChange={handleRunsChange}
                disabled={readOnly}
                aria-label="Degree of parallelism"
              />
            </Field>
          </div>

          {isTrigger && (
            <div className={styles.field}>
              <Field label="Max Waiting Runs" hint="Maximum queued runs (optional)">
                <Input
                  type="number"
                  min={0}
                  value={String(currentSettings.maximumWaitingRuns ?? '')}
                  onChange={handleWaitingRunsChange}
                  placeholder="Unlimited"
                  disabled={readOnly}
                  aria-label="Maximum waiting runs"
                />
              </Field>
            </div>
          )}
        </div>
      )}

      {currentSettings.enabled && (
        <Text className={styles.hint}>
          {isTrigger
            ? 'Controls how many trigger instances can run simultaneously'
            : 'Controls how many action instances can run in parallel within a workflow'}
        </Text>
      )}
    </div>
  );
};

ConcurrencyRenderer.displayName = 'ConcurrencyRenderer';

// Register this renderer
registerSettingRenderer('concurrency', ConcurrencyRenderer);
